import React, { useState } from "react";
import { Link } from "react-router-dom";
import styled from "styled-components";

import {
  HSpace,
  Text,
  VSpace,
  Label,
  CheckboxInput,
} from "@examspark/common-ui";
import { LabelText } from "@examspark/common-ui/dist/components/inputs/Label";
import { Objective } from "@examspark/types";

const StyledTable = styled.table`
  tr td {
    border-top: solid 1px #ddd;
    padding: 1rem 0.5rem;
    vertical-align: top;
  }
`;
const StyledAbbrCell = styled.td`
  width: 10rem;
`;

export const StyledCheckboxLabel = styled(Label)`
  & > input[type="checkbox"] {
    flex-grow: 0;
    order: -1;
  }

  & > ${LabelText} {
    text-transform: initial;
  }
`;

const ElpsTable = () => {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <StyledTable cellPadding={0} cellSpacing={0}>
      <tr>
        <td colSpan={2}>
          <VSpace>
            <Text.Body>
              <p>
                The English Language Proficiency Standards (ELPS), as required
                by 19 Texas Administrative Code, Chapter 74, Subchapter A,
                §74.4, outline English language proficiency level descriptors
                and student expectations for English language learners (ELLs).
                School districts are required to implement ELPS as an integral
                part of each subject in the required curriculum.
              </p>
              <p>
                School districts shall provide instruction in the knowledge and
                skills of the foundation and enrichment curriculum in a manner
                that is linguistically accommodated commensurate with the
                student’s levels of English language proficiency to ensure that
                the student learns the knowledge and skills in the required
                curriculum. School districts shall provide content-based
                instruction including the cross-curricular second language
                acquisition essential knowledge and skills in subsection (c) of
                the ELPS in a manner that is linguistically accommodated to help
                the student acquire English language proficiency.
              </p>
            </Text.Body>
            <HSpace size="2rem">
              <StyledCheckboxLabel label="Show all ELPS" layout="row">
                <CheckboxInput
                  checked={isExpanded}
                  onChange={({ checked }) => setIsExpanded(!isExpanded)}
                />
              </StyledCheckboxLabel>
              <Link
                target="_blank"
                to="https://tea.texas.gov/about-tea/laws-and-rules/texas-administrative-code/texas-administrative-code-title-19-part-2"
                rel="noopener noreferer"
              >
                Learn more about ELPS
              </Link>
            </HSpace>
          </VSpace>
        </td>
      </tr>
      {isExpanded && (
        <>
          {elps.map((objective) => (
            <React.Fragment key={objective.abbreviation}>
              <tr style={{ backgroundColor: "#eee", fontWeight: "bold" }}>
                <StyledAbbrCell>{objective.abbreviation}</StyledAbbrCell>
                <td>
                  <Text.Body>{objective.content}</Text.Body>
                </td>
              </tr>
              {objective.children?.map((child) => (
                <React.Fragment key={objective.abbreviation}>
                  <tr>
                    <StyledAbbrCell>{child.abbreviation}</StyledAbbrCell>
                    <td>
                      <Text.Body>{child.content}</Text.Body>
                    </td>
                  </tr>
                </React.Fragment>
              ))}
            </React.Fragment>
          ))}
        </>
      )}
    </StyledTable>
  );
};

export default ElpsTable;

type ObjectiveTreeItem = Partial<Objective> & {
  children?: ObjectiveTreeItem[];
};

const elps: ObjectiveTreeItem[] = [
  {
    abbreviation: "ELPS.c.1",
    content:
      "The ELL uses language learning strategies to develop an awareness of his or her own learning processes in all content areas. In order for the ELL to meet grade-level learning expectations across the foundation and enrichment curriculum, all instruction delivered in English must be linguistically accommodated (communicated, sequenced, and scaffolded) commensurate with the student's level of English language proficiency. The student is expected to:",
    children: [
      {
        abbreviation: "ELPS.c.1.A",
        content:
          "use prior knowledge and experiences to understand meanings in English",
      },
      {
        abbreviation: "ELPS.c.1.B",
        content:
          "monitor oral and written language production and employ self-corrective techniques or other resources",
      },
      {
        abbreviation: "ELPS.c.1.C",
        content:
          "use strategic learning techniques such as concept mapping, drawing, memorizing, comparing, contrasting, and reviewing to acquire basic and grade-level vocabulary",
      },
      {
        abbreviation: "ELPS.c.1.D",
        content:
          "speak using learning strategies such as requesting assistance, employing non-verbal cues, and using synonyms and circumlocution (conveying ideas by defining or describing when exact English words are not known)",
      },
      {
        abbreviation: "ELPS.c.1.E",
        content:
          "internalize new basic and academic language by using and reusing it in meaningful ways in speaking and writing activities that build concept and language attainment",
      },
      {
        abbreviation: "ELPS.c.1.F",
        content:
          "use accessible language and learn new and essential language in the process",
      },
      {
        abbreviation: "ELPS.c.1.G",
        content:
          "demonstrate an increasing ability to distinguish between formal and informal English and an increasing knowledge of when to use each one commensurate with grade-level learning expectations",
      },
      {
        abbreviation: "ELPS.c.1.H",
        content:
          "develop and expand repertoire of learning strategies such as reasoning inductively or deductively, looking for patterns in language, and analyzing sayings and expressions commensurate with grade-level learning expectations.",
      },
    ],
  },
  {
    abbreviation: "ELPS.c.2",
    content:
      "The ELL listens to a variety of speakers including teachers, peers, and electronic media to gain an increasing level of comprehension of newly acquired language in all content areas. ELLs may be at the beginning, intermediate, advanced, or advanced high stage of English language acquisition in listening. In order for the ELL to meet grade-level learning expectations across the foundation and enrichment curriculum, all instruction delivered in English must be linguistically accommodated (communicated, sequenced, and scaffolded) commensurate with the student's level of English language proficiency. The student is expected to:",
    children: [
      {
        abbreviation: "ELPS.c.2.A",
        content:
          "distinguish sounds and intonation patterns of English with increasing ease",
      },
      {
        abbreviation: "ELPS.c.2.B",
        content:
          "recognize elements of the English sound system in newly acquired vocabulary such as long and short vowels, silent letters, and consonant clusters",
      },
      {
        abbreviation: "ELPS.c.2.C",
        content:
          "learn new language structures, expressions, and basic and academic vocabulary heard during classroom instruction and interactions",
      },
      {
        abbreviation: "ELPS.c.2.D",
        content:
          "monitor understanding of spoken language during classroom instruction and interactions and seek clarification as needed",
      },
      {
        abbreviation: "ELPS.c.2.E",
        content:
          "use visual, contextual, and linguistic support to enhance and confirm understanding of increasingly complex and elaborated spoken language",
      },
      {
        abbreviation: "ELPS.c.2.F",
        content:
          "listen to and derive meaning from a variety of media such as audio tape, video, DVD, and CD ROM to build and reinforce concept and language attainment",
      },
      {
        abbreviation: "ELPS.c.2.G",
        content:
          "understand the general meaning, main points, and important details of spoken language ranging from situations in which topics, language, and contexts are familiar to unfamiliar",
      },
      {
        abbreviation: "ELPS.c.2.H",
        content:
          "understand implicit ideas and information in increasingly complex spoken language commensurate with grade-level learning expectations",
      },
      {
        abbreviation: "ELPS.c.2.I",
        content:
          "demonstrate listening comprehension of increasingly complex spoken English by following directions, retelling or summarizing spoken messages, responding to questions and requests, collaborating with peers, and taking notes commensurate with content and grade-level needs.",
      },
    ],
  },
  {
    abbreviation: "ELPS.c.3",
    content:
      "The ELL speaks in a variety of modes for a variety of purposes with an awareness of different language registers (formal/informal) using vocabulary with increasing fluency and accuracy in language arts and all content areas. ELLs may be at the beginning, intermediate, advanced, or advanced high stage of English language acquisition in speaking. In order for the ELL to meet grade-level learning expectations across the foundation and enrichment curriculum, all instruction delivered in English must be linguistically accommodated (communicated, sequenced, and scaffolded) commensurate with the student's level of English language proficiency. The student is expected to:",
    children: [
      {
        abbreviation: "ELPS.c.3.A",
        content:
          "practice producing sounds of newly acquired vocabulary such as long and short vowels, silent letters, and consonant clusters to pronounce English words in a manner that is increasingly comprehensible",
      },
      {
        abbreviation: "ELPS.c.3.B",
        content:
          "expand and internalize initial English vocabulary by learning and using high-frequency English words necessary for identifying and describing people, places, and objects, by retelling simple stories and basic information represented or supported by pictures, and by learning and using routine language needed for classroom communication",
      },
      {
        abbreviation: "ELPS.c.3.C",
        content:
          "speak using a variety of grammatical structures, sentence lengths, sentence types, and connecting words with increasing accuracy and ease as more English is acquired",
      },
      {
        abbreviation: "ELPS.c.3.D",
        content:
          "speak using grade-level content area vocabulary in context to internalize new English words and build academic language proficiency",
      },
      {
        abbreviation: "ELPS.c.3.E",
        content: "share information in cooperative learning interactions",
      },
      {
        abbreviation: "ELPS.c.3.F",
        content:
          "ask and give information ranging from using a very limited bank of high-frequency, high-need, concrete vocabulary, including key words and expressions needed for basic communication in academic and social contexts, to using abstract and content-based vocabulary during extended speaking assignments",
      },
      {
        abbreviation: "ELPS.c.3.G",
        content:
          "express opinions, ideas, and feelings ranging from communicating single words and short phrases to participating in extended discussions on a variety of social and grade-appropriate academic topics",
      },
      {
        abbreviation: "ELPS.c.3.H",
        content:
          "narrate, describe, and explain with increasing specificity and detail as more English is acquired",
      },
      {
        abbreviation: "ELPS.c.3.I",
        content:
          "adapt spoken language appropriately for formal and informal purposes",
      },
      {
        abbreviation: "ELPS.c.3.J",
        content:
          "respond orally to information presented in a wide variety of print, electronic, audio, and visual media to build and reinforce concept and language attainment.",
      },
    ],
  },
  {
    abbreviation: "ELPS.c.4",
    content:
      "The ELL reads a variety of texts for a variety of purposes with an increasing level of comprehension in all content areas. ELLs may be at the beginning, intermediate, advanced, or advanced high stage of English language acquisition in reading. In order for the ELL to meet grade-level learning expectations across the foundation and enrichment curriculum, all instruction delivered in English must be linguistically accommodated (communicated, sequenced, and scaffolded) commensurate with the student's level of English language proficiency. For Kindergarten and Grade 1, certain of these student expectations apply to text read aloud for students not yet at the stage of decoding written text. The student is expected to:",
    children: [
      {
        abbreviation: "ELPS.c.4.A",
        content:
          "learn relationships between sounds and letters of the English language and decode (sound out) words using a combination of skills such as recognizing sound-letter relationships and identifying cognates, affixes, roots, and base words",
      },
      {
        abbreviation: "ELPS.c.4.B",
        content:
          "recognize directionality of English reading such as left to right and top to bottom",
      },
      {
        abbreviation: "ELPS.c.4.C",
        content:
          "develop basic sight vocabulary, derive meaning of environmental print, and comprehend English vocabulary and language structures used routinely in written classroom materials",
      },
      {
        abbreviation: "ELPS.c.4.D",
        content:
          "use prereading supports such as graphic organizers, illustrations, and pretaught topic-related vocabulary and other prereading activities to enhance comprehension of written text",
      },
      {
        abbreviation: "ELPS.c.4.E",
        content:
          "read linguistically accommodated content area material with a decreasing need for linguistic accommodations as more English is learned",
      },
      {
        abbreviation: "ELPS.c.4.F",
        content:
          "use visual and contextual support and support from peers and teachers to read grade-appropriate content area text, enhance and confirm understanding, and develop vocabulary, grasp of language structures, and background knowledge needed to comprehend increasingly challenging language",
      },
      {
        abbreviation: "ELPS.c.4.G",
        content:
          "demonstrate comprehension of increasingly complex English by participating in shared reading, retelling or summarizing material, responding to questions, and taking notes commensurate with content area and grade level needs",
      },
      {
        abbreviation: "ELPS.c.4.H",
        content:
          "read silently with increasing ease and comprehension for longer periods",
      },
      {
        abbreviation: "ELPS.c.4.I",
        content:
          "demonstrate English comprehension and expand reading skills by employing basic reading skills such as demonstrating understanding of supporting ideas and details in text and graphic sources, summarizing text, and distinguishing main ideas from details commensurate with content area needs",
      },
      {
        abbreviation: "ELPS.c.4.J",
        content:
          "demonstrate English comprehension and expand reading skills by employing inferential skills such as predicting, making connections between ideas, drawing inferences and conclusions from text and graphic sources, and finding supporting text evidence commensurate with content area needs",
      },
      {
        abbreviation: "ELPS.c.4.K",
        content:
          "demonstrate English comprehension and expand reading skills by employing analytical skills such as evaluating written information and performing critical analyses commensurate with content area and grade-level needs.",
      },
    ],
  },
  {
    abbreviation: "ELPS.c.5",
    content:
      "The ELL writes in a variety of forms with increasing accuracy to effectively address a specific purpose and audience in all content areas. ELLs may be at the beginning, intermediate, advanced, or advanced high stage of English language acquisition in writing. In order for the ELL to meet grade-level learning expectations across foundation and enrichment curriculum, all instruction delivered in English must be linguistically accommodated (communicated, sequenced, and scaffolded) commensurate with the student's level of English language proficiency. For Kindergarten and Grade 1, certain of these student expectations do not apply until the student has reached the stage of generating original written text using a standard writing system. The student is expected to:",
    children: [
      {
        abbreviation: "ELPS.c.5.A",
        content:
          "learn relationships between sounds and letters of the English language to represent sounds when writing in English",
      },
      {
        abbreviation: "ELPS.c.5.B",
        content:
          "write using newly acquired basic vocabulary and content-based grade-level vocabulary",
      },
      {
        abbreviation: "ELPS.c.5.C",
        content:
          "spell familiar English words with increasing accuracy, and employ English spelling patterns and rules with increasing accuracy as more English is acquired",
      },
      {
        abbreviation: "ELPS.c.5.D",
        content:
          "edit writing for standard grammar and usage, including subject-verb agreement, pronoun agreement, and appropriate verb tenses commensurate with grade-level expectations as more English is acquired",
      },
      {
        abbreviation: "ELPS.c.5.E",
        content:
          "employ increasingly complex grammatical structures in content area writing commensurate with grade-level expectations, such as:",
      },
      {
        abbreviation: "ELPS.c.5.F",
        content:
          "write using a variety of grade-appropriate sentence lengths, patterns, and connecting words to combine phrases, clauses, and sentences in increasingly accurate ways as more English is acquired",
      },
      {
        abbreviation: "ELPS.c.5.G",
        content:
          "narrate, describe, and explain with increasing specificity and detail to fulfill content area writing needs as more English is acquired.",
      },
    ],
  },
];
